import React, { useState, useRef, useEffect } from 'react';
import { X, Upload, Trash2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import type { SequenceMedia } from '../types';
import { s3Client } from '../config/aws';
import { PutObjectCommand, DeleteObjectCommand } from '@aws-sdk/client-s3';

const BUCKET_NAME = 'xrmgpublic';
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const TEMP_PREFIX = 'internal/barker/temp/';

interface MediaDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (media: SequenceMedia) => void;
  onDelete: () => void;
  sentenceIndex: number;
  existingMedia?: SequenceMedia;
  cloudFolder?: string;
  isPublic?: boolean;
  sequenceName: string;
}

function MediaDialog({ 
  isOpen, 
  onClose, 
  onSave, 
  onDelete,
  sentenceIndex,
  existingMedia,
  cloudFolder,
  isPublic = false,
  sequenceName
}: MediaDialogProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const previousSentenceIndex = useRef(sentenceIndex);

  // Reset state when the dialog opens/closes or when sentence changes
  useEffect(() => {
    if (!isOpen) {
      setPreviewUrl(null);
      setError(null);
      return;
    }

    // Check if we've switched to a different sentence
    if (previousSentenceIndex.current !== sentenceIndex) {
      console.log('MediaDialog: Sentence index changed, resetting state');
      setPreviewUrl(null);
      setError(null);
      previousSentenceIndex.current = sentenceIndex;
    }

    // Set preview URL from existing media
    if (existingMedia?.url) {
      console.log('MediaDialog: Setting preview URL from existing media:', existingMedia.url);
      setPreviewUrl(existingMedia.url);
    } else {
      console.log('MediaDialog: No existing media found');
      setPreviewUrl(null);
    }
  }, [isOpen, sentenceIndex, existingMedia]);

  if (!isOpen) return null;

  const generateTempS3Key = (file: File) => {
    const mediaId = uuidv4();
    const ext = file.name.split('.').pop();
    const key = `${TEMP_PREFIX}${mediaId}.${ext}`;
    console.log('MediaDialog: Generated temp S3 key:', key);
    return key;
  };

  const generateFinalS3Key = (tempKey: string) => {
    const fileName = tempKey.split('/').pop() || '';
    const safeSequenceName = sequenceName.toLowerCase().replace(/[^a-z0-9]/g, '-');
    const prefix = isPublic 
      ? `internal/barker/public/media/${safeSequenceName}/` 
      : `internal/barker/userdata/${cloudFolder}/media/${safeSequenceName}/`;
    const key = `${prefix}${fileName}`;
    console.log('MediaDialog: Generated final S3 key:', key);
    return key;
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      console.log('MediaDialog: No file selected');
      return;
    }

    console.log('MediaDialog: File selected:', {
      name: file.name,
      type: file.type,
      size: file.size
    });

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      console.log('MediaDialog: Invalid file type:', file.type);
      setError('Invalid file type. Please upload a JPEG, PNG, or GIF file.');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      console.log('MediaDialog: File too large:', file.size);
      setError('File size exceeds 5MB limit.');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      // First upload to temp location
      const tempS3Key = generateTempS3Key(file);
      console.log('MediaDialog: Converting file to buffer');
      const fileBuffer = await file.arrayBuffer();
      
      console.log('MediaDialog: Uploading to temp location:', tempS3Key);
      const uploadCommand = new PutObjectCommand({
        Bucket: BUCKET_NAME,
        Key: tempS3Key,
        Body: new Uint8Array(fileBuffer),
        ContentType: file.type
      });

      await s3Client.send(uploadCommand);
      console.log('MediaDialog: Upload successful');

      // Generate preview URL
      const tempUrl = `https://${BUCKET_NAME}.s3.amazonaws.com/${tempS3Key}`;
      console.log('MediaDialog: Setting preview URL:', tempUrl);
      setPreviewUrl(tempUrl);

      // Create media object with temp location
      const finalPath = generateFinalS3Key(tempS3Key);
      const media: SequenceMedia = {
        id: uuidv4(),
        sentenceIndex,
        type: file.type === 'image/gif' ? 'gif' : 'image',
        url: tempUrl,
        cloudPath: tempS3Key,
        finalPath
      };

      console.log('MediaDialog: Saving media object:', media);
      onSave(media);
    } catch (error) {
      console.error('MediaDialog: Error uploading media:', error);
      setError('Failed to upload media. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!existingMedia?.cloudPath) {
      console.log('MediaDialog: No cloud path for existing media, calling onDelete');
      onDelete();
      return;
    }

    console.log('MediaDialog: Deleting media from S3:', existingMedia.cloudPath);
    setIsUploading(true);
    setError(null);

    try {
      const command = new DeleteObjectCommand({
        Bucket: BUCKET_NAME,
        Key: existingMedia.cloudPath
      });

      await s3Client.send(command);
      console.log('MediaDialog: Delete successful');
      onDelete();
    } catch (error) {
      console.error('MediaDialog: Error deleting media:', error);
      setError('Failed to delete media. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {existingMedia ? 'Edit Media' : 'Add Media'}
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* Current Media Display */}
          {previewUrl && (
            <div className="relative">
              <img 
                src={previewUrl}
                alt="Media preview"
                className="w-full h-64 object-contain rounded-lg shadow-md"
                onError={(e) => {
                  console.error('MediaDialog: Error loading preview image:', previewUrl);
                  const img = e.target as HTMLImageElement;
                  img.style.display = 'none';
                }}
                onLoad={() => console.log('MediaDialog: Preview image loaded successfully:', previewUrl)}
              />
              <button
                onClick={handleDelete}
                disabled={isUploading}
                className="mt-4 flex items-center justify-center w-full px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                <Trash2 size={20} className="mr-2" />
                <span>Remove Media</span>
              </button>
            </div>
          )}

          {/* Upload Interface */}
          {!previewUrl && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="flex items-center justify-center w-full px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 focus:outline-none focus:border-blue-500"
              >
                <div className="text-center">
                  <Upload size={32} className="mx-auto text-gray-400 mb-2" />
                  <span className="text-gray-600">
                    {isUploading ? 'Uploading...' : 'Click to upload image or GIF'}
                  </span>
                  <p className="text-sm text-gray-500 mt-1">
                    JPEG, PNG, or GIF up to 5MB
                  </p>
                </div>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MediaDialog;